import React, {
	type ComponentPropsWithRef,
	type ForwardedRef,
	Component,
	type ReactNode,
	type ComponentType,
	forwardRef,
} from 'react';
import noop from 'lodash/noop';
import ScrollSynchronizerContext, { type SyncAxis } from '../context';

type Props = {
	children: ReactNode;
	innerRef: (arg1: HTMLElement | null) => void;
	forwardedRef: ForwardedRef<HTMLElement>;
};

type Config = {
	syncAxis: SyncAxis | undefined;
	isCompiledComponent?: boolean;
};

const withSynchronizedScroll = <TProps extends Props, TComponent extends ComponentType<TProps>>(
	WrappedComponent: TComponent,
	{ syncAxis, isCompiledComponent }: Config = { syncAxis: undefined, isCompiledComponent: false },
) => {
	// eslint-disable-next-line jira/react/no-class-components
	class WithSynchronizedScroll extends Component<TProps> {
		static defaultProps = {
			innerRef: noop,
		};

		// go/jfe-eslint
		// eslint-disable-next-line react/sort-comp
		static contextType = ScrollSynchronizerContext;

		componentDidMount() {
			// @ts-expect-error - TS2339 - Property 'register' does not exist on type 'unknown'.
			const { register } = this.context;

			if (this.ref) {
				register(this.ref, syncAxis);
			}
		}

		componentWillUnmount() {
			// @ts-expect-error - TS2339 - Property 'unregister' does not exist on type 'unknown'.
			const { unregister } = this.context;

			if (this.ref) {
				unregister(this.ref);
			}
		}

		onSetRef = (elem: HTMLElement | null) => {
			const { innerRef, forwardedRef } = this.props;

			if (forwardedRef != null && typeof forwardedRef === 'function') {
				forwardedRef(elem);
			}
			this.ref = elem;
			innerRef(elem);
		};

		// @ts-expect-error - TS2564 - Property 'ref' has no initializer and is not definitely assigned in the constructor.
		ref: HTMLElement | null;

		render() {
			const { children, ...props } = this.props;
			const refProp = isCompiledComponent
				? { ref: this.onSetRef, innerRef: undefined }
				: { innerRef: this.onSetRef };

			return (
				// @ts-expect-error - TS2769 - No overload matches this call.
				<WrappedComponent {...props} {...refProp}>
					{children}
				</WrappedComponent>
			);
		}
	}

	return forwardRef<HTMLElement, ComponentPropsWithRef<TComponent>>((props, ref) => (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
		<WithSynchronizedScroll {...(props as any)} forwardedRef={ref} />
	));
};

export default withSynchronizedScroll;
